import * as React from "react";
import * as LucideIcons from "lucide-react";

import { cn } from "@/lib/utils";
import { Button as ShadcnButton } from "@/components/ui/button";

// Shared shadcn primitives for the legacy Pages Router views. The compatibility
// exports keep the existing screen markup stable while using only local UI code.

const Div = React.forwardRef<HTMLDivElement, any>(({ className, ...props }, ref) => (
  <div ref={ref} className={className} {...props} />
));
Div.displayName = "Div";

const GridRow = ({ className, cards, deck, dimmer, ...props }: any) => (
  <div className={cn("flex w-full flex-wrap", cards && "-mx-2", className)} {...props} />
);
const columnWidths: Record<string, string> = {
  "1": "w-full sm:w-1/12", "1.5": "w-full sm:w-[12.5%]", "2": "w-full sm:w-2/12",
  "2.5": "w-full sm:w-[20.833333%]", "3": "w-full sm:w-3/12", "4": "w-full sm:w-4/12",
  "5": "w-full sm:w-5/12", "6": "w-full sm:w-6/12", "7": "w-full sm:w-7/12",
  "8": "w-full sm:w-8/12", "9": "w-full sm:w-9/12", "10": "w-full sm:w-10/12",
  "11": "w-full sm:w-11/12", "12": "w-full sm:w-full",
};
const GridCol = ({ className, width, xs, sm, md, lg, xl, ...props }: any) => {
  const span = width || xl || lg || md || sm || xs;
  return (
    <div
      className={cn("min-w-0 px-2", span ? columnWidths[String(Math.min(Number(span), 12))] || "w-full" : "flex-1", className)}
      {...props}
    />
  );
};
export const Grid: any = { Row: GridRow, Col: GridCol };

const CardRoot = React.forwardRef<HTMLDivElement, any>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("rounded-lg border border-border bg-background text-foreground shadow-sm", className)} {...props} />
));
CardRoot.displayName = "Card";
export const Card: any = Object.assign(CardRoot, {
  Header: ({ className, ...props }: any) => <div className={cn("flex items-center border-b border-border px-6 py-4", className)} {...props} />,
  Body: ({ className, ...props }: any) => <div className={cn("p-6", className)} {...props} />,
  Footer: ({ className, ...props }: any) => <div className={cn("flex items-center border-t border-border px-6 py-4", className)} {...props} />,
  Title: ({ className, ...props }: any) => <h3 className={cn("text-lg font-semibold", className)} {...props} />,
  Options: ({ className, ...props }: any) => <div className={cn("ml-auto", className)} {...props} />,
});

const colorToVariant = (color?: string, outline?: boolean, link?: boolean) => {
  if (link) return "link";
  if (outline) return "outline";
  if (color === "danger" || color === "red") return "destructive";
  if (color === "secondary" || color === "dark") return "secondary";
  return "default";
};
const LegacyButton = ({ className, color, block, loading, link, outline, icon, children, disabled, RootComponent, ...props }: any) => {
  const classes = cn(block && "w-full", className);
  const content = loading ? "Loading…" : <>{icon && <Icon name={icon} size={18} />}{children}</>;
  if (RootComponent) {
    const Component = RootComponent;
    return <Component className={cn("inline-flex items-center justify-center gap-2", classes)} aria-disabled={Boolean(disabled || loading)} {...props}>{content}</Component>;
  }
  return <ShadcnButton className={classes} variant={colorToVariant(color, outline, link)} disabled={Boolean(disabled || loading)} {...props}>{content}</ShadcnButton>;
};
export const Button: any = Object.assign(LegacyButton, {
  List: ({ className, ...props }: any) => <div className={cn("flex flex-wrap gap-2", className)} {...props} />,
});

const FormRoot = ({ className, ...props }: any) => <form className={cn("space-y-4", className)} {...props} />;
const FormGroup = ({ className, label, children, description, ...props }: any) => (
  <div className={cn("space-y-2", className)} {...props}>
    {label && <label className="block text-sm font-medium">{label}</label>}
    {children}
    {description && <p className="text-sm text-muted-foreground">{description}</p>}
  </div>
);
const inputClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";
const FormInput = ({ className, invalid, valid, ...props }: any) => <input className={cn(inputClass, invalid && "border-destructive", className)} {...props} />;
const FormFileInput = ({ className, invalid, valid, ...props }: any) => <input type="file" className={cn(inputClass, "pt-1.5", invalid && "border-destructive", className)} {...props} />;
const FormSelect = ({ className, invalid, valid, ...props }: any) => <select className={cn(inputClass, invalid && "border-destructive", className)} {...props} />;
const FormTextarea = ({ className, invalid, valid, ...props }: any) => <textarea className={cn("min-h-24", inputClass, invalid && "border-destructive", className)} {...props} />;
const FormCheckbox = ({ className, label, children, ...props }: any) => (
  <label className={cn("flex items-center gap-2 text-sm", className)}>
    <input type="checkbox" className="h-4 w-4 rounded border-input" {...props} />
    {label || children}
  </label>
);
export const Form: any = Object.assign(FormRoot, {
  Group: FormGroup,
  Input: FormInput,
  FileInput: FormFileInput,
  Select: FormSelect,
  Textarea: FormTextarea,
  Checkbox: FormCheckbox,
  InputGroup: ({ className, ...props }: any) => <div className={cn("flex", className)} {...props} />,
  InputGroupAppend: ({ className, ...props }: any) => <div className={cn("flex items-center", className)} {...props} />,
  Footer: ({ className, ...props }: any) => <div className={cn("flex gap-2", className)} {...props} />,
});
export const FormTextInput = ({ label, error, className, ...props }: any) => (
  <FormGroup label={label} className={className}>
    <FormInput {...props} invalid={Boolean(error)} />
    {error && <p className="text-sm text-destructive">{error}</p>}
  </FormGroup>
);
export const FormCheckboxInput = ({ label, error, className, ...props }: any) => (
  <FormGroup className={className}>
    <FormCheckbox label={label} {...props} />
    {error && <p className="text-sm text-destructive">{error}</p>}
  </FormGroup>
);

function iconName(name = "circle") {
  return name
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");
}
export const Icon = ({ name, prefix, size = 18, className, ...props }: any) => {
  if (prefix === "fa") return <i className={cn("fa", `fa-${name}`, className)} {...props} />;
  const Candidate = (LucideIcons as any)[iconName(name)] || LucideIcons.Circle;
  return <Candidate size={size} className={cn("inline-block align-text-bottom", className)} aria-hidden="true" {...props} />;
};

const header = (Tag: any) => ({ className, ...props }: any) => <Tag className={cn("font-semibold tracking-tight", className)} {...props} />;
export const Header: any = Object.assign(header("h2"), {
  H1: header("h1"), H2: header("h2"), H3: header("h3"), H4: header("h4"), H5: header("h5"), H6: header("h6"),
});
export const Text: any = Object.assign(({ className, ...props }: any) => <span className={className} {...props} />, {
  Small: ({ className, ...props }: any) => <small className={cn("text-sm text-muted-foreground", className)} {...props} />,
});

export const Dimmer = ({ active, children, className, ...props }: any) => (
  <div className={cn("relative", className)} aria-busy={active || undefined} {...props}>
    {children}
    {active && <div className="absolute inset-0 grid place-items-center rounded bg-background/65 text-sm text-muted-foreground">Loading…</div>}
  </div>
);

export const Tag: any = Object.assign(({ className, color, children, ...props }: any) => (
  <span className={cn("inline-flex items-center rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium", color === "success" && "bg-green-100 text-green-800", className)} {...props}>{children}</span>
), {
  List: ({ className, ...props }: any) => <div className={cn("flex flex-wrap gap-2", className)} {...props} />,
});
export const Alert = ({ className, bsStyle, type, onDismiss, isDismissible, children, ...props }: any) => (
  <div className={cn("relative rounded-md border border-border bg-background px-4 py-3 text-sm shadow-sm", (bsStyle === "danger" || type === "danger") && "border-destructive text-destructive", className)} role="alert" {...props}>
    {children}
    {isDismissible && <button type="button" className="absolute right-2 top-2 text-muted-foreground" onClick={onDismiss} aria-label="Dismiss">×</button>}
  </div>
);

export const List: any = Object.assign(({ className, unstyled, ...props }: any) => <ul className={cn(!unstyled && "list-disc", className)} {...props} />, {
  Item: ({ className, ...props }: any) => <li className={className} {...props} />,
});
export const Timeline: any = Object.assign(({ className, ...props }: any) => <ol className={cn("space-y-4 border-l border-border pl-5", className)} {...props} />, {
  Item: ({ className, title, children, ...props }: any) => <li className={cn("relative", className)} {...props}><span className="absolute -left-[1.65rem] top-1 h-2.5 w-2.5 rounded-full bg-primary" />{title && <h4 className="font-medium">{title}</h4>}{children}</li>,
});

export const Site: any = Object.assign(({ className, ...props }: any) => <header className={cn("border-b border-border bg-background", className)} {...props} />, {
  Header: ({ className, ...props }: any) => <div className={cn("container mx-auto flex min-h-16 items-center px-4", className)} {...props} />,
  Nav: ({ className, ...props }: any) => <nav className={cn("border-t border-border", className)} {...props} />,
});
export const Nav: any = Object.assign(({ className, ...props }: any) => <div className={cn("container mx-auto flex items-center gap-3 px-4 py-2", className)} {...props} />, {
  Item: ({ className, to, value, children, ...props }: any) => <a href={to || "#"} className={cn("text-sm text-muted-foreground hover:text-foreground", className)} {...props}>{children || value}</a>,
});
export const Page = Div;
export const Container = ({ className, ...props }: any) => <div className={cn("container mx-auto px-4", className)} {...props} />;

export const BlogCard = ({ title, postUrl, description, avatarImgSrc, authorName, date, imgSrc, imgAlt }: any) => (
  <Card><img src={imgSrc} alt={imgAlt || ""} className="h-44 w-full rounded-t-lg object-cover" /><Card.Body><Card.Title>{title}</Card.Title><p className="mt-2 text-sm text-muted-foreground">{description}</p><a className="mt-4 inline-block text-sm text-primary hover:underline" href={postUrl}>Read more</a><div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground"><img className="h-6 w-6 rounded-full" src={avatarImgSrc} alt="" />{authorName} · {date}</div></Card.Body></Card>
);
export const AccountDropdown = ({ avatarURL, name, description, options = [] }: any) => <details className="relative"><summary className="flex cursor-pointer list-none items-center gap-2"><img className="h-8 w-8 rounded-full" src={avatarURL} alt="" /><span><span className="block text-sm font-medium">{name}</span><span className="block text-xs text-muted-foreground">{description}</span></span></summary><div className="absolute right-0 z-20 mt-2 w-48 rounded-md border border-border bg-background p-2 shadow-lg">{options.map((option: any, index: number) => option === "divider" ? <hr key={index} className="my-2" /> : <a key={index} href={typeof option === "string" ? `/${option}` : option.to} className="block rounded px-2 py-1.5 text-sm hover:bg-accent">{typeof option === "string" ? option : option.value}</a>)}</div></details>;

const AuthPage = ({ onSubmit, onChange, onBlur, values = {}, errors = {}, touched = {}, register }: any) => (
  <form className="space-y-4 p-6" onSubmit={onSubmit}>
    {register && <FormGroup label="Name"><FormInput name="name" value={values.name || ""} onChange={onChange} onBlur={onBlur} /><p className="text-sm text-destructive">{touched.name && errors.name}</p></FormGroup>}
    <FormGroup label="Email"><FormInput name="email" type="email" value={values.email || ""} onChange={onChange} onBlur={onBlur} /><p className="text-sm text-destructive">{touched.email && errors.email}</p></FormGroup>
    <FormGroup label="Password"><FormInput name="password" type="password" value={values.password || ""} onChange={onChange} onBlur={onBlur} /><p className="text-sm text-destructive">{touched.password && errors.password}</p></FormGroup>
    {register && <FormCheckbox name="terms" checked={values.terms || false} onChange={onChange} label="I agree to the terms and policy" />}
    <LegacyButton type="submit" block>{register ? "Create account" : "Sign in"}</LegacyButton>
  </form>
);
export const RegisterPage = (props: any) => <AuthPage {...props} register />;
